import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { GlobeIcon, PlusIcon } from 'lucide-react';
import { PERMISSIONS } from '@siteflow/shared';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { EmptyState } from '@/components/empty-state';
import { PermissionGate } from '@/components/permission-gate';
import { useSiteScope } from '@/components/sites/site-scope';
import { StatusChip } from '@/components/status-chip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { messageForError } from '@/lib/api-client';
import { siteKeys, sitesApi } from '@/lib/api/sites';
import type { DomainVerificationStatus, SiteDomain } from '@/lib/api/types';
import { formatDate } from '@/lib/utils';

const VERIFICATION: Record<
  DomainVerificationStatus,
  { tone: 'active' | 'pending' | 'danger' | 'neutral'; label: string; note: string }
> = {
  verified: {
    tone: 'active',
    label: 'Verified',
    note: 'Ownership confirmed. Licence checks accept this hostname.',
  },
  pending: {
    tone: 'pending',
    label: 'Pending',
    note: 'A verification record has been issued and is being checked.',
  },
  unverified: {
    tone: 'neutral',
    label: 'Unverified',
    note: 'No verification has been attempted. Activate a licence against this environment to start one.',
  },
  failed: {
    tone: 'danger',
    label: 'Failed',
    note: 'The expected record was not found. Check the DNS entry and try again.',
  },
  expired: {
    tone: 'danger',
    label: 'Expired',
    note: 'The verification lapsed and needs to be repeated.',
  },
};

function DomainsTab() {
  const { orgId, siteId } = Route.useParams();
  const queryClient = useQueryClient();
  const { site, environments } = useSiteScope();

  const [addOpen, setAddOpen] = useState(false);
  const [hostname, setHostname] = useState('');
  const [environmentId, setEnvironmentId] = useState(environments[0]?.id ?? '');
  const [isPrimary, setIsPrimary] = useState(false);
  const [removing, setRemoving] = useState<SiteDomain | null>(null);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: siteKeys.all(orgId) });
  }

  const addMutation = useMutation({
    mutationFn: () =>
      sitesApi.addDomain(orgId, siteId, {
        hostname: hostname.trim(),
        siteEnvironmentId: environmentId,
        isPrimary,
      }),
    onSuccess: (domain) => {
      toast.success(`${domain.hostname} attached`);
      setAddOpen(false);
      setHostname('');
      setIsPrimary(false);
      invalidate();
    },
    onError: (error) => toast.error(messageForError(error, 'That domain could not be attached.')),
  });

  const removeMutation = useMutation({
    mutationFn: (domainId: string) => sitesApi.removeDomain(orgId, siteId, domainId),
    onSuccess: () => {
      toast.success('Domain removed');
      setRemoving(null);
      invalidate();
    },
    onError: (error) => toast.error(messageForError(error, 'That domain could not be removed.')),
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-prose">
          <h2 className="text-lede font-semibold tracking-tight text-ink">Domains</h2>
          <p className="text-data leading-relaxed text-ink-muted">
            The hostnames each environment is served from. Verifying a domain proves you control it,
            which is what a licence with domain binding checks before it accepts an activation.
          </p>
        </div>
        <PermissionGate permission={PERMISSIONS.SITE_DOMAINS_MANAGE}>
          <Button onClick={() => setAddOpen(true)}>
            <PlusIcon />
            Add domain
          </Button>
        </PermissionGate>
      </div>

      {site.domains.length === 0 ? (
        <EmptyState
          icon={GlobeIcon}
          title="No domains attached"
          description="Attach the hostname each environment is reached at. Without one, licence checks fall back to the environment's base URL."
          action={
            <PermissionGate permission={PERMISSIONS.SITE_DOMAINS_MANAGE}>
              <Button onClick={() => setAddOpen(true)}>Add the first domain</Button>
            </PermissionGate>
          }
        />
      ) : (
        <ul className="flex flex-col rounded-lg border border-hairline bg-elevated">
          {site.domains.map((domain) => {
            const meta = VERIFICATION[domain.verificationStatus];
            const environment = environments.find((item) => item.id === domain.siteEnvironmentId);
            return (
              <li
                key={domain.id}
                className="flex flex-wrap items-center gap-3 border-b border-hairline px-4 py-3 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 truncate font-mono text-data text-ink">
                    {domain.hostname}
                    {domain.isPrimary ? <Badge variant="accent">Primary</Badge> : null}
                  </p>
                  <p className="text-micro text-ink-subtle">
                    {environment?.name ?? 'Unknown environment'} · added{' '}
                    <span data-numeric>{formatDate(domain.createdAt)}</span>
                    {domain.verifiedAt ? (
                      <> · verified {formatDate(domain.verifiedAt)}</>
                    ) : null}
                  </p>
                  <p className="max-w-prose text-micro leading-relaxed text-ink-subtle">
                    {meta.note}
                  </p>
                </div>
                <StatusChip tone={meta.tone}>{meta.label}</StatusChip>
                <PermissionGate permission={PERMISSIONS.SITE_DOMAINS_MANAGE}>
                  <Button variant="outlineDanger" size="sm" onClick={() => setRemoving(domain)}>
                    Remove
                  </Button>
                </PermissionGate>
              </li>
            );
          })}
        </ul>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a domain</DialogTitle>
            <DialogDescription>
              Attach a hostname to one environment. Verification is started separately, when a
              licence that requires it is activated.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="domain-host">Hostname</Label>
              <Input
                id="domain-host"
                value={hostname}
                placeholder="www.example.com"
                className="font-mono"
                onChange={(event) => setHostname(event.target.value)}
              />
              <p className="text-micro text-ink-subtle">
                No scheme and no path — just the hostname.
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="domain-env">Environment</Label>
              <Select value={environmentId} onValueChange={setEnvironmentId}>
                <SelectTrigger id="domain-env">
                  <SelectValue placeholder="Choose one" />
                </SelectTrigger>
                <SelectContent>
                  {environments.map((environment) => (
                    <SelectItem key={environment.id} value={environment.id}>
                      {environment.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-start gap-3">
              <Switch id="domain-primary" checked={isPrimary} onCheckedChange={setIsPrimary} />
              <div className="flex flex-col gap-0.5">
                <Label htmlFor="domain-primary">Primary domain</Label>
                <p className="text-micro text-ink-subtle">
                  The canonical hostname for this environment.
                </p>
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!hostname.trim() || !environmentId || addMutation.isPending}
              onClick={() => addMutation.mutate()}
            >
              {addMutation.isPending ? 'Attaching…' : 'Attach domain'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        destructive
        title="Remove this domain?"
        confirmLabel="Remove domain"
        pending={removeMutation.isPending}
        description={
          <>
            <strong className="text-ink">{removing?.hostname}</strong> and its verification state are
            deleted. If a licence requires domain verification, activations bound to this hostname
            stop validating.
          </>
        }
        onConfirm={() => removing && removeMutation.mutate(removing.id)}
      />
    </div>
  );
}

export const Route = createFileRoute('/app/$orgId/sites/$siteId/domains')({
  component: DomainsTab,
});
